import {
  selectListAllVehiclesInDb,
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDbStatus,
} from "./../vehicles/vehiclesSlice";
import {
  driversListAsync,
  selectDriversList,
  selectDriversListStatus,
} from "./../drivers/driversSlice";
import {
  cardsListAsync,
  selectCardsList,
  selectCardsListStatus,
} from "../cardsAndBadges/cards/cardsSlice";
import { selectCompany } from "./../homeSlice";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DataCard, Spacer, TextCapitalized } from "@qivia/ui";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  badgesListAsync,
  selectBadgesList,
  selectBadgesListStatus,
} from "../cardsAndBadges/badges/badgesSlice";
import { VehiclesListType } from "../vehicles/vehiclesAPI";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { DriversListType } from "../drivers/driversAPI";
import { BadgesList } from "../cardsAndBadges/badges/badgesAPI";
import { CardsListType } from "../cardsAndBadges/cards/cardsAPI";
import "../../../i18n/i18n";

export const DashboardSummaryCard = (props: {
  count: number;
  label: string;
}) => {
  return (
    <DataCard
      titleHeight=""
      title={
        <>
          <Spacer y={0.5} />
          <StyledTitle>{props.count.toString()}</StyledTitle>
        </>
      }
      content={<TextCapitalized>{props.label}</TextCapitalized>}
      backgroundColor={`${colors["colors/button/secondary/hover"]}`}
      width={15.5}
      borderRadius={0.5}
      withoutBorder
    />
  );
};

export const DashboardTop = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);

  const allVehiclesInDb = useAppSelector(selectListAllVehiclesInDb);
  const driversList = useAppSelector(selectDriversList);
  const cardsList = useAppSelector(selectCardsList);
  const badgesList = useAppSelector(selectBadgesList);
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const driversListStatus = useAppSelector(selectDriversListStatus);
  const cardsListStatus = useAppSelector(selectCardsListStatus);
  const badgesListStatus = useAppSelector(selectBadgesListStatus);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [dispatch, company, listAllVehiclesInDbStatus]);

  useEffect(() => {
    if (company && driversListStatus === "idle") {
      void dispatch(driversListAsync(company.uuid));
    }
  }, [dispatch, company, driversListStatus]);

  useEffect(() => {
    if (company && cardsListStatus === "idle") {
      void dispatch(cardsListAsync(company.uuid));
    }
  }, [dispatch, company, cardsListStatus]);

  useEffect(() => {
    if (company && badgesListStatus === "idle") {
      void dispatch(badgesListAsync(company.uuid));
    }
  }, [dispatch, company, badgesListStatus]);

  const vehiclesActiveList: VehiclesListType[] = useMemo(() => {
    return allVehiclesInDb.filter(
      (vehicle) => vehicle.status === "IN_CIRCULATION",
    );
  }, [allVehiclesInDb]);

  const driversActiveList: DriversListType[] = useMemo(() => {
    return driversList.filter((driver) => driver.status === "ACTIVE");
  }, [driversList]);

  const cardsActiveList: CardsListType[] = useMemo(() => {
    return cardsList.filter((card) => card.status === "ACTIVE");
  }, [cardsList]);

  const badgesActiveList: BadgesList[] = useMemo(() => {
    return badgesList.filter((badge) => badge.status === "ACTIVE");
  }, [badgesList]);

  return (
    <StyledSummary>
      <StyleFlex>
        <StyledCardsContainer>
          <DashboardSummaryCard
            label={t("dashboard.card.vehicles")}
            count={vehiclesActiveList.length}
          />
          <DashboardSummaryCard
            label={t("dashboard.card.drivers")}
            count={driversActiveList.length}
          />
          <DashboardSummaryCard
            label={t("dashboard.card.cards")}
            count={cardsActiveList.length}
          />
          <DashboardSummaryCard
            label={t("dashboard.card.badges")}
            count={badgesActiveList.length}
          />
        </StyledCardsContainer>
        <Spacer x={1.5} />
      </StyleFlex>
      <Spacer y={2.5} />
    </StyledSummary>
  );
};

const StyledTitle = styled.div`
  display: flex;
  font-size: 2rem;
  font-weight: 500;
`;

const StyledCardsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
  gap: 1.5rem;
`;

const StyleFlex = styled.div`
  display: flex;
`;

const StyledSummary = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 70rem;
`;
